import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHardTherapistList } from '../../APICalling/APICalling';
import { useGlobalContext } from '../../Context/GlobalContext';
import { formatDateToDDMMYYYY } from '../../Utils/utils';
import Echo from '../Sockect/Echo';
import styles from './HardQueueSidebar.module.css';
import Loader from '../Loader/Loader';
import refreshIcon from '../../Assets/images/refresh.svg';

const HardQueueSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { selectedOutlet, selectedDate, selectedTime, homeStaffList } = useGlobalContext();
    const [selectedServices, setSelectedServices] = useState([1,34,5]);
    const [hardQueueTherapists, setHardQueueTherapists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const attendanceChannelRef = useRef(null);

    // Clear data when sidebar closes
    useEffect(() => {
        if (!isOpen) {
            setHardQueueTherapists([]);
            setLoading(false);
            setHasFetched(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && selectedOutlet && selectedDate) {
            // Clear previous data and set loading immediately to prevent flicker
            setHardQueueTherapists([]);
            setLoading(true);
            setHasFetched(false);
            
            // Only fetch if services are selected, otherwise show empty state
            if (selectedServices && selectedServices.length > 0) {
                fetchHardQueueTherapists();
            } else {
                setHardQueueTherapists([]);
                setLoading(false);
                setHasFetched(true);
            }
        }
    }, [isOpen, selectedOutlet, selectedDate, selectedServices]);

    // Attendance check-in updates this local event immediately after the API succeeds.
    useEffect(() => {
        if (!isOpen) return;

        const handleStaffTimingUpdate = () => {
            if (selectedOutlet && selectedDate && selectedServices && selectedServices.length > 0) {
                fetchHardQueueTherapists();
            }
        };

        window.addEventListener('staffTimingUpdate', handleStaffTimingUpdate);
        return () => window.removeEventListener('staffTimingUpdate', handleStaffTimingUpdate);
    }, [isOpen, selectedOutlet, selectedDate, selectedServices]);

    // Listen for attendance.updated socket event and refresh hard queue
    useEffect(() => {
        if (!isOpen) return;

        if (!Echo) return;

        const channelName = "attendance";
        const eventName = "attendance.updated";

        try {
            const channel = Echo.channel(channelName);
            attendanceChannelRef.current = channel;

            // Listen for attendance.updated event (with and without dot prefix)
            channel.listen(eventName, (event) => {
                if (selectedOutlet && selectedDate && selectedServices && selectedServices.length > 0) {
                    fetchHardQueueTherapists();
                }
            });

            channel.listen(`.${eventName}`, (event) => {
                if (selectedOutlet && selectedDate && selectedServices && selectedServices.length > 0) {
                    fetchHardQueueTherapists();
                }
            });

        } catch (error) {
            console.error("Error setting up attendance socket listener:", error);
        }

        // Cleanup: unsubscribe from channel when component unmounts or sidebar closes
        return () => {
            if (attendanceChannelRef.current) {
                try {
                    attendanceChannelRef.current.stopListening(eventName);
                    attendanceChannelRef.current.stopListening(`.${eventName}`);
                    Echo.leave(channelName);
                    attendanceChannelRef.current = null;
                } catch (error) {
                    // Silent cleanup error
                }
            }
        };
    }, [isOpen, selectedOutlet, selectedDate, selectedServices]);

    const fetchHardQueueTherapists = async () => {
        // if (!selectedOutlet || !selectedDate || !selectedServices || selectedServices.length === 0) {
        //     return;
        // }

        // Loading is already set to true in useEffect to prevent flicker
        try {
            // Get service IDs from selectedServices
            const serviceIds = selectedServices.map(svc => {
                // Handle both number and object formats
                if (typeof svc === 'number') return svc;
                return svc.service_id || svc.id || svc.value;
            }).filter(Boolean);
            
            if (serviceIds.length === 0) {
                setHardQueueTherapists([]);
                setLoading(false);
                return;
            }

            // Calculate total duration from selected services
            const totalDuration = selectedServices.reduce((sum, svc) => {
                if (typeof svc === 'number') return sum + 30; // Default duration for number IDs
                return sum + (Number(svc.duration) || 30);
            }, 0);
            
            // Format date as DD-MM-YYYY
            const formattedDate = formatDateToDDMMYYYY(selectedDate);
            
            // Get current Singapore time in 24-hour format (HH:MM)
            const time24 = getCurrentSingaporeTime();
            
            // Build API params - format: DD-MM-YYYY HH:MM
            // Service should be comma-separated string: "1,34,5"
            const params = {
                outlet: selectedOutlet,
                service_at: `${formattedDate} ${time24}`,
                // duration: totalDuration || 30,
                // service: serviceIds.join(',')
            };

            const response = await getHardTherapistList(params);
            
            // API response structure: { data: { success: true, data: [...] } }
            if (response?.data?.success && response?.data?.data) {
                setHardQueueTherapists(response.data.data);
            } else {
                setHardQueueTherapists([]);
            }
            setHasFetched(true);
        } catch (error) {
            console.error('Error fetching hard queue therapists:', error);
            setHardQueueTherapists([]);
            setHasFetched(true);
        } finally {
            setLoading(false);
        }
    };

    const formatCheckinTime = (checkinTime) => {
        if (!checkinTime) return 'N/A';
        
        try {
            // Parse the datetime string (format: "2025-11-25 16:59:12")
            const date = new Date(checkinTime);
            if (isNaN(date.getTime())) return 'N/A';
            
            // Format as "HH:MM" (24-hour format)
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const getCurrentSingaporeTime = () => {
        // Get current time in Singapore timezone (UTC+8)
        const now = new Date();
        const formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: "Asia/Singapore",
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
        });
        const parts = formatter.formatToParts(now);
        let hours = parseInt(parts.find(part => part.type === 'hour').value);
        let minutes = parseInt(parts.find(part => part.type === 'minute').value);
        
        minutes += 1;
        if (minutes >= 60) {
            minutes = minutes % 60;
            hours = (hours + 1) % 24;
        }
        
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    const getPagerNumber = (therapistName) => {
        if (!therapistName || !homeStaffList || homeStaffList.length === 0) {
            return null;
        }
        
        // Try to find matching therapist by name or alias
        const matchedTherapist = homeStaffList.find(staff => {
            const staffName = (staff.name || '').toLowerCase().trim();
            const staffAlias = (staff.alias || '').toLowerCase().trim();
            const therapistNameLower = therapistName.toLowerCase().trim();
            
            return staffName === therapistNameLower || staffAlias === therapistNameLower;
        });
        
        return matchedTherapist?.pager_number || null;
    };

    const getTherapistId = (therapistName) => {
        if (!therapistName || !homeStaffList || homeStaffList.length === 0) {
            return null;
        }
        
        // Try to find matching therapist by name or alias
        const matchedTherapist = homeStaffList.find(staff => {
            const staffName = (staff.name || '').toLowerCase().trim();
            const staffAlias = (staff.alias || '').toLowerCase().trim();
            const therapistNameLower = therapistName.toLowerCase().trim();
            
            return staffName === therapistNameLower || staffAlias === therapistNameLower;
        });
        
        return matchedTherapist?.id || null;
    };

    const getTherapistGender = (therapist, therapistName) => {
        if (!homeStaffList || homeStaffList.length === 0) {
            return null;
        }
        
        // Fallback to name/alias matching
        if (therapistName) {
            const therapistNameLower = therapistName.toLowerCase().trim();
            const matchedTherapist = homeStaffList.find(staff => {
                const staffName = (staff.name || '').toLowerCase().trim();
                const staffAlias = (staff.alias || '').toLowerCase().trim();
                
                return staffName === therapistNameLower || staffAlias === therapistNameLower;
            });
            
            if (matchedTherapist?.gender) {
                return matchedTherapist.gender;
            }
        }
        
        return null;
    };

    const handleTherapistClick = (therapist) => {
        // Get therapist ID from the therapist object or lookup from homeStaffList
        const therapistId = therapist.id || therapist.therapist_id || getTherapistId(therapist.therapist);
        
        if (therapistId) {
            // Navigate to therapists page with therapist ID and details tab
            navigate(`/therapists?therapist=${therapistId}&tab=details`);
            // Close the sidebar after navigation
            if (onClose) {
                onClose();
            }
        }
    };

    const getInitials = (name) => {
        if (!name) return '';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const handleManualRefresh = () => {
        if (selectedOutlet && selectedDate && selectedServices && selectedServices.length > 0) {
            setLoading(true);
            fetchHardQueueTherapists();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.sidebar}>
                <div className={styles.header}>
                    <h3 className={styles.title}>Checked in therapist hard queue</h3>
                    <button 
                        className={styles.refreshButton}
                        onClick={handleManualRefresh}
                        title="Refresh list"
                        disabled={loading}
                    >
                        <img 
                            src={refreshIcon} 
                            alt="Refresh" 
                            className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
                        />
                    </button>
                </div>
                
                <div className={styles.content}>
                    {loading || !hasFetched ? (
                        <div className={styles.loaderContainer}>
                            <Loader />
                        </div>
                    ) : (!selectedServices || selectedServices.length === 0) ? (
                        <div className={styles.emptyState}>
                            <p>Please select services to view hard queue therapists</p>
                        </div>
                    ) : hardQueueTherapists.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No therapists available</p>
                        </div>
                    ) : (
                        <ul className={styles.therapistList}>
                            {hardQueueTherapists.map((therapist, index) => (
                                <li 
                                    key={therapist.therapist || index} 
                                    className={styles.therapistItem}
                                    onClick={() => handleTherapistClick(therapist)}
                                >
                                    <div className={styles.therapistInfo}>
                                        <div className={`${styles.pagerBadge} ${(() => {
                                            const gender = therapist.gender || getTherapistGender(therapist, therapist.therapist);
                                            const isMale = gender && (gender.toLowerCase() === 'male' || gender.toLowerCase() === 'm');
                                            return isMale ? styles.blue : styles.pink;
                                        })()}`}>
                                            {therapist.pager_number || therapist.pager || getPagerNumber(therapist.therapist) || getInitials(therapist.therapist)}
                                        </div>
                                        <div className={styles.therapistDetails}>
                                            <span
                                                className={styles.therapistName}
                                                title={therapist.therapist}
                                            >
                                                {therapist.therapist}
                                            </span>
                                            <span className={styles.statusText}>
                                                {therapist.busy_till ? (
                                                    <><em>Busy till</em> <strong>{formatCheckinTime(therapist.busy_till)}</strong></>
                                                ) : (
                                                    <><em>Checked in</em> <strong>{formatCheckinTime(therapist.checkin_time)}</strong></>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
        </div>
    );
};

export default HardQueueSidebar;
